import type {
  DataQualityReport,
  EvaluationMethodConfig,
  EvaluationMethodDefinition,
  EvaluationMethodKey,
  EvaluationRun,
} from './sweetSpotEvaluationEngine';
import {
  buildEvaluationConfigSignature,
  buildEvaluationInputSignature,
} from './sweetSpotEvaluationEngine';
import type { SweetSpotInputPoint } from './types';

export type SemanticTone = 'neutral' | 'current' | 'success' | 'warning' | 'error';
export type DataState = 'unpublished' | 'partial' | 'complete';
export type CalculationState = 'idle' | 'running' | 'complete' | 'stale' | 'failed';
export type ResultState =
  | 'none'
  | 'draft'
  | 'current'
  | 'historical'
  | 'parameters_changed'
  | 'data_changed'
  | 'method_changed'
  | 'unverifiable';

export type SweetSpotWorkflowStepKey =
  | 'data-check'
  | 'method'
  | 'parameters'
  | 'execute'
  | 'review'
  | 'save'
  | 'apply';

export type SweetSpotWorkflowStepStatus =
  | 'not_started'
  | 'pending'
  | 'completed'
  | 'warning'
  | 'disabled';

export type SweetSpotWorkflowStep = {
  key: SweetSpotWorkflowStepKey;
  order: number;
  label: string;
  status: SweetSpotWorkflowStepStatus;
  disabled: boolean;
  reason?: string;
};

export type StatusMeta = { label: string; tone: SemanticTone };

export const DATA_STATE_META: Record<DataState, StatusMeta> = {
  unpublished: { label: '未应用', tone: 'warning' },
  partial: { label: '部分缺失', tone: 'warning' },
  complete: { label: '数据完整', tone: 'success' },
};

export const CALCULATION_STATE_META: Record<CalculationState, StatusMeta> = {
  idle: { label: '未计算', tone: 'neutral' },
  running: { label: '评价正在执行', tone: 'current' },
  complete: { label: '计算完成', tone: 'success' },
  stale: { label: '待更新', tone: 'warning' },
  failed: { label: '计算失败', tone: 'error' },
};

export const RESULT_STATE_META: Record<ResultState, StatusMeta> = {
  none: { label: '无评价结果', tone: 'neutral' },
  draft: { label: '未保存版本', tone: 'current' },
  current: { label: '当前版本', tone: 'success' },
  historical: { label: '历史版本', tone: 'neutral' },
  parameters_changed: { label: '参数已变更', tone: 'warning' },
  data_changed: { label: '输入数据已变更', tone: 'warning' },
  method_changed: { label: '方法版本已变更', tone: 'warning' },
  unverifiable: { label: '历史版本（无法校验）', tone: 'neutral' },
};

export const RUN_STATUS_LABELS: Record<EvaluationRun['status'], string> = {
  draft: '未保存版本',
  saved: '已保存',
  syncFailed: '应用失败',
  active: '当前版本',
};

export const TONE_TAG_COLORS: Record<SemanticTone, string> = {
  neutral: 'default',
  current: 'processing',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

export const SWEETSPOT_WORKFLOW_LABELS: Record<SweetSpotWorkflowStepKey, string> = {
  'data-check': '数据检查',
  method: '评价指标',
  parameters: '权重方案',
  execute: '执行评价',
  review: '结果审核',
  save: '保存版本',
  apply: '应用压裂设计',
};

export const SWEETSPOT_WORKFLOW_STATUS_LABELS: Record<SweetSpotWorkflowStepStatus, string> = {
  not_started: '未开始',
  pending: '待确认',
  completed: '已完成',
  warning: '警告',
  disabled: '禁用',
};

export function deriveSweetSpotWorkflowSteps(options: {
  dataState: DataState;
  calculationState: CalculationState;
  configValid: boolean;
  canCalculate: boolean;
  hasResult: boolean;
  canSaveResult: boolean;
  hasSavedResult: boolean;
  canApplyResult: boolean;
  isActiveResult: boolean;
  reviewState?: EvaluationMethodConfig['reviewState'];
}): SweetSpotWorkflowStep[] {
  const dataReady = options.dataState !== 'unpublished';
  const dataComplete = options.dataState === 'complete';
  const executionReady = dataReady && options.configValid && options.canCalculate;
  const reviewComplete = options.reviewState === 'reviewed' || options.reviewState === 'confirmed';

  const rows: Array<Omit<SweetSpotWorkflowStep, 'order' | 'label'>> = [
    {
      key: 'data-check',
      status: !dataReady ? 'warning' : dataComplete ? 'completed' : 'warning',
      disabled: false,
      reason: !dataReady ? '当前数据尚未在数据中心应用。' : dataComplete ? undefined : '存在缺失指标，请确认缺失值处理策略。',
    },
    {
      key: 'method',
      status: !dataReady ? 'disabled' : 'completed',
      disabled: !dataReady,
      reason: !dataReady ? '完成数据检查后可确认评价指标。' : undefined,
    },
    {
      key: 'parameters',
      status: !dataReady ? 'disabled' : options.configValid ? 'completed' : 'warning',
      disabled: !dataReady,
      reason: !dataReady ? '完成数据检查后可选择权重方案。' : options.configValid ? undefined : '评价指标、权重或等级阈值无效。',
    },
    {
      key: 'execute',
      status: !executionReady
        ? 'disabled'
        : options.calculationState === 'running'
          ? 'pending'
          : options.hasResult && options.calculationState === 'complete'
            ? 'completed'
            : options.calculationState === 'stale' ? 'warning' : 'pending',
      disabled: !executionReady,
      reason: executionReady ? undefined : '数据或评价配置尚未满足执行条件。',
    },
    {
      key: 'review',
      status: !options.hasResult ? 'disabled' : reviewComplete ? 'completed' : 'pending',
      disabled: !options.hasResult,
      reason: options.hasResult ? undefined : '执行评价后可审核结果。',
    },
    {
      key: 'save',
      status: options.hasSavedResult
        ? 'completed'
        : !options.hasResult || !options.canSaveResult ? 'disabled' : 'pending',
      disabled: !options.hasSavedResult && (!options.hasResult || !options.canSaveResult),
      reason: options.hasSavedResult
        ? undefined
        : !options.hasResult ? '执行评价后可保存版本。'
          : !options.canSaveResult ? '当前结果未通过正式保存检查。' : undefined,
    },
    {
      key: 'apply',
      status: options.isActiveResult
        ? 'completed'
        : !options.hasSavedResult || !options.canApplyResult ? 'disabled' : 'pending',
      disabled: !options.isActiveResult && (!options.hasSavedResult || !options.canApplyResult),
      reason: options.isActiveResult
        ? undefined
        : !options.hasSavedResult ? '请先保存评价版本。'
          : !options.canApplyResult ? '当前版本未满足应用条件。' : undefined,
    },
  ];

  return rows.map((row, index) => ({
    ...row,
    order: index + 1,
    label: SWEETSPOT_WORKFLOW_LABELS[row.key],
  }));
}

export function deriveDataState(
  publishedCurveCount: number,
  quality: DataQualityReport,
): DataState {
  if (publishedCurveCount <= 0 || quality.pointCount <= 0) return 'unpublished';
  return quality.completeness >= 0.999 && quality.missingIndicatorIds.length === 0
    ? 'complete'
    : 'partial';
}
export function getRunResultState(options: {
  run?: EvaluationRun;
  activeRunId?: string;
  methodKey: EvaluationMethodKey;
  definition: EvaluationMethodDefinition;
  config: EvaluationMethodConfig;
  points: SweetSpotInputPoint[];
  sourceCurveIds?: string[];
}): ResultState {
  const run = options.run;
  if (!run) return 'none';
  if (run.methodKey !== options.methodKey
    || run.methodVersion !== options.definition.version
    || run.definitionHash !== options.definition.definitionHash) return 'method_changed';
  if (run.inputSnapshot.configSignature
    && run.inputSnapshot.configSignature !== buildEvaluationConfigSignature(options.config)) {
    return 'parameters_changed';
  }
  if (run.inputSnapshot.inputSignature
    && run.inputSnapshot.inputSignature !== buildEvaluationInputSignature(options.points, options.sourceCurveIds)) {
    return 'data_changed';
  }
  if (!run.inputSnapshot.configSignature || !run.inputSnapshot.inputSignature) return 'unverifiable';
  if (run.status === 'draft') return 'draft';
  return run.runId === options.activeRunId ? 'current' : 'historical';
}

export function deriveCalculationState(options: {
  busy?: boolean;
  error?: string;
  resultState: ResultState;
}): CalculationState {
  if (options.busy) return 'running';
  if (options.error) return 'failed';
  if (['parameters_changed', 'data_changed', 'method_changed'].includes(options.resultState)) return 'stale';
  return options.resultState === 'none' ? 'idle' : 'complete';
}

export function evaluationActionLabel(state: CalculationState): string {
  if (state === 'complete') return '重新评价';
  if (state === 'stale') return '更新评价结果';
  return '执行评价';
}

export function evaluationDisabledReason(options: {
  busy?: boolean;
  dataState: DataState;
  quality: DataQualityReport;
  hasInterval: boolean;
  configValid: boolean;
}): string | undefined {
  if (options.busy) return '评价正在执行';
  if (!options.hasInterval) return '评价井段未设置';
  if (options.dataState === 'unpublished') return '当前数据尚未在数据中心应用';
  if (!options.configValid) return '参数配置无效';
  if (!options.quality.canCalculate) {
    return options.quality.issues.find((issue) => issue.severity === 'error')?.message
      || '缺少必要测井曲线';
  }
  return undefined;
}

export function shortSignature(signature?: string): string {
  if (!signature) return '未记录';
  const value = signature.includes(':') ? signature.split(':').pop() || signature : signature;
  return value.slice(0, 8).toUpperCase();
}
